import React, {useCallback, useRef, useEffect, useMemo} from 'react';
import {debounce} from 'ts-debounce';

import BenchlingConfig from 'BenchlingConfig';
import {RealtimeMessage} from 'realtime/RealtimeChannel';
import useRealtimeChannelSubscription, {MessageFilter} from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId, isCurrentClient} from 'realtime/util/RealtimeUtil';

export interface RealtimeUpdateSubscriptionWrapperProps {
  apiId?: string;
  messageFilter: MessageFilter;
  refetch: () => unknown;
  children: JSX.Element | null;
  /**
   * Will refresh even when receiving messages which originated from the same client.
   * This is useful for managing updates to sub-tabs which may be affected by actions from another tab but are
   * difficult to update via the GQL cache.
   */
  refetchOnMessagesFromSameClient?: boolean;
  /**
   * Tells this wrapper whether to enqueue a refetch in five seconds upon rendering. Only one refetch will be
   * enqueued at a time, since the component maintains internal state.
   *
   * This is to work around the following issue with apollo:
   * - This component receives a message and tells apollo to refetch X_QUERY
   * - Apollo fires X_QUERY, but this query takes a few seconds
   * - This component receives another message and tells apollo to refetch X_QUERY
   * - Because apollo detects X_QUERY is in progress, it doesn't enqueue a second X_QUERY request
   * - But the server may return stale data for X_QUERY
   *
   * If there are no more messages coming on this channel, the component will get "stuck" and never
   * refetch the latest data until the page is refreshed.
   *
   * This prop lets the calling component tell the wrapper to enqueue a final refetch. (Which means the calling
   * component needs to be able to determine if the data is incomplete or not.)
   */
  hasIncompleteDataShouldRefetch?: boolean;
  disabled?: boolean;
}

export const HALF_SEC = 500;
export const FIVE_SECS = 5000;

function RealtimeUpdateSubscriptionWrapperInternal(
  props: RealtimeUpdateSubscriptionWrapperProps & {apiId: string}
): JSX.Element | null {
  const {disabled, refetch, refetchOnMessagesFromSameClient, messageFilter} = props;

  // We're going to debounce our refetch. However, the refetch prop
  // can change on rerenders so we can't just `debounce(props.refetch)`.
  // Instead, we need to keep a ref for the current value of props.refetch,
  // so we can call it from our debounced function.
  //
  // (`useEffect` or `useCallback` with `props.refetch` as a dependency does not work here
  // because react can't properly diff two functions.)
  const refetchRef = useRef<typeof refetch>(props.refetch);
  useEffect(() => {
    refetchRef.current = props.refetch;
  }, [props.refetch]);

  const tryInvokeRefetch = (): void => {
    if (refetchRef.current != null) {
      refetchRef.current();
    }
  };

  const finalRefetchTimeoutId = useRef<ReturnType<typeof setTimeout>>();
  const clearFinalRefetchTimeoutId = (): void => {
    if (finalRefetchTimeoutId.current != null) {
      clearTimeout(finalRefetchTimeoutId.current);
    }
  };

  // since we are using a timeout in this component, we need to make sure to clear it
  // before the component unmounts
  useEffect(
    () => () => {
      clearFinalRefetchTimeoutId();
    },
    []
  );

  // The debounced function also has to live within a ref, so that it maintains its state
  // across renders
  const refetcher = useMemo(
    () =>
      // Debouncing helps (1) reduce the number of calls we make to the DB and (2) prevents multiple refetches from
      // "colliding" if the client receives multiple messages near the same time.
      // https://github.com/apollographql/apollo-client/issues/9308
      debounce(
        () => {
          tryInvokeRefetch();
        },
        HALF_SEC,
        {maxWait: FIVE_SECS, isImmediate: false}
      ),
    []
  );

  clearFinalRefetchTimeoutId();
  // Enqueue an additional refetch for insurance. See prop description.
  if (props.hasIncompleteDataShouldRefetch) {
    finalRefetchTimeoutId.current = setTimeout(() => {
      tryInvokeRefetch();
    }, FIVE_SECS);
  }

  const onMessage = useCallback(
    (message: RealtimeMessage) => {
      if (BenchlingConfig.ELN_ENABLE_REALTIME_UPDATES_M1 && !disabled) {
        if (
          refetchOnMessagesFromSameClient ||
          message.data?.clientId == null ||
          !isCurrentClient(message.data.clientId)
        ) {
          refetcher();
        }
      }
    },
    [disabled, refetcher, refetchOnMessagesFromSameClient]
  );

  useRealtimeChannelSubscription(getRealtimeChannelNameForApiId(props.apiId), onMessage, messageFilter);
  return props.children;
}

export default function RealtimeUpdateSubscriptionWrapper(
  props: RealtimeUpdateSubscriptionWrapperProps
): JSX.Element | null {
  return props.apiId ? (
    <RealtimeUpdateSubscriptionWrapperInternal {...props} apiId={props.apiId} />
  ) : (
    props.children
  );
}
